var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-buildify-dropzone.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import "@adornis/dragdrop/x-drag-drop.js";
import "@adornis/fonts/fonts.js";
import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { Base } from "../db/Base.js";
import { BaseContainer } from "../db/extensions/BaseContainer.js";
import { BaseCustomContent } from "../db/extensions/BaseCustomContent.js";
import {
  ClipboardConsumer,
  ContentConsumer,
  DraggingConsumer,
  ExtensionsConsumer,
  IdentifierConsumer,
  ModeConsumer
} from "./globals/consumer.js";
import { Droppable } from "./globals/enums.js";
import { ThrowEventCopyToClipboard, ThrowEventSetContent } from "./globals/events.js";
import { deepCopyBaseArray } from "./globals/methods.js";
import "./globals/types.js";
var XBuildifyDropzone = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.index = new RXController(this, void 0);
    this.parentID = new RXController(this, null);
    this.subItemID = new RXController(this, null);
    this.showOnDragOver = new RXController(this, false);
    this.noSwapIndexes = new RXController(this, false);
    this._consumedDragging = DraggingConsumer(this);
    this._consumedContent = ContentConsumer(this);
    this._consumedClipboard = ClipboardConsumer(this);
    this._consumedIdentifier = IdentifierConsumer(this, false);
    this._consumedExtensions = ExtensionsConsumer(this);
    this._consumedMode = ModeConsumer(this);
  }
  render() {
    if (this._consumedMode.value === "view")
      return nothing;
    if (this.showOnDragOver.value)
      return this.__dragOverDropzone();
    return this.__dropzone();
  }
  _dropped(e) {
    const type = e.detail.type;
    const value = e.detail.value;
    let instance;
    let isNewValue = true;
    const additionalInstances = [];
    const elementsOnRoot = (this._consumedContent.value ?? []).filter((base) => !base.parentID).sort((a, b) => a.index - b.index);
    const indexOfLastElement = elementsOnRoot[elementsOnRoot.length - 1] ? elementsOnRoot[elementsOnRoot.length - 1].index + 1 : 1;
    const content = this._consumedContent.value ?? [];
    switch (type) {
      case Droppable.EXTENSION:
        if (!value)
          throw new Error("invalid value for draggable extension");
        instance = new value.class({});
        break;
      case Droppable.CLIPBOARD:
        const clipboard = this._consumedClipboard.value;
        if (!clipboard)
          throw new Error("no clipboard value was found");
        const root = clipboard[0];
        instance = root;
        clipboard?.splice(0, 1);
        const children = clipboard;
        additionalInstances.push(...children);
        ThrowEventCopyToClipboard(this, null);
        break;
      case Droppable.EXISTING_CONTENT:
        if (!(value instanceof Base))
          throw new Error("no base was as existing content provided");
        if (value instanceof BaseContainer)
          this._checkContainerDroppedIntoSelf(value._id, this.parentID.value);
        instance = value;
        isNewValue = false;
        break;
      case Droppable.CUSTOM_CONTENT_REFERENCE:
        if (!(value instanceof BaseCustomContent))
          throw new Error("value does not match to BaseCustomContent");
        instance = value;
        break;
      case Droppable.CUSTOM_CONTENT_TEMPLATE:
        if (!Array.isArray(value))
          return;
        const copiedContents = deepCopyBaseArray({
          content: value,
          extensions: this._consumedExtensions.value ?? []
        });
        const startIndex = this.index.value ?? indexOfLastElement ?? 1;
        let variableIndex = startIndex;
        for (const content2 of copiedContents.filter((c) => !c.parentID).sort((a, b) => a.index - b.index)) {
          content2.parentID = this.parentID.value;
          content2.subItemID = this.subItemID.value;
          content2.index = variableIndex;
          variableIndex++;
        }
        const copiedContentIDs = copiedContents.map((c) => c._id);
        content.push(...copiedContents);
        content.filter(
          (b) => b.index >= startIndex && !copiedContentIDs.includes(b._id) && (b.parentID === this.parentID.value && b.subItemID === this.subItemID.value || !b.parentID && !this.parentID.value)
        ).forEach((c) => c.index = c.index + (variableIndex - startIndex));
        return ThrowEventSetContent(this, content);
      default:
        throw new Error("something weird got dropped in here O.o");
    }
    if (!instance)
      throw new Error("no instance was set");
    instance.index = this.index.value ?? indexOfLastElement ?? 1;
    instance.parentID = this.parentID.value;
    instance.subItemID = this.subItemID.value;
    if (!this.noSwapIndexes.value) {
      content.filter(
        (base) => base.index >= instance.index && base._id !== instance._id && (base.parentID === instance.parentID || !base.parentID && !instance.parentID)
      ).forEach((base) => {
        base.index = base.index + 1;
      });
    }
    if (isNewValue) {
      content.push(instance);
      content.push(...additionalInstances);
    }
    ThrowEventSetContent(this, content);
  }
  _checkContainerDroppedIntoSelf(_id, newParentID) {
    if (!newParentID)
      return;
    if (_id === newParentID)
      throw new Error("you can't drag a container into itself");
    const newParent = this._consumedContent.value?.find((base) => base._id === newParentID);
    if (!newParent)
      throw new Error("how did that happen?");
    this._checkContainerDroppedIntoSelf(_id, newParent.parentID);
  }
  __dropzone() {
    return html`
      <x-drag-drop
        dropzone
        allow-from="d-button"
        child-selector="[movable]"
        dropzone-accept-selector=".bar-item[identifier='${this._consumedIdentifier.value}']"
        @dropped=${async (e) => {
      this._dropped(e);
    }}
      >
        <x-flex class="box-wrapper" w100 center crossaxis-center horizontal padding="xs" space="sm">
          <x-icon> place_item </x-icon> <x-text> Komponente hier fallen lassen... </x-text>
        </x-flex>
      </x-drag-drop>
    `;
  }
  __dragOverDropzone() {
    if (!this._consumedDragging.value)
      return nothing;
    return html`
      <x-flex
        h100
        w100
        ${css({
      position: "relative"
    })}
      >
        <x-drag-drop
          ${css({ height: "100%" })}
          dropzone
          dropzone-accept-selector=".bar-item[identifier='${this._consumedIdentifier.value}']"
          @dropped=${async (e) => {
      this._dropped(e);
    }}
        >
          <x-flex dropzone-indicator crossaxis-center center horizontal space="sm" w100 h100> </x-flex>
        </x-drag-drop>
      </x-flex>
    `;
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          overflow: "visible",
          zIndex: "999",
          width: "100%",
          userSelect: "none",
          display: !this._consumedDragging.value && this.showOnDragOver.value ? "none" : ""
        },
        ...this.showOnDragOver.value ? {
          "[dropzone]": {
            borderRadius: this.sizes.borderRadius,
            width: "100%",
            "&>[dropzone-indicator]": {
              width: "100%",
              backgroundColor: "#5DE2E7",
              color: this.colors.on.accent.shade(100),
              opacity: "0",
              transition: "opacity 200ms"
            },
            "&[dropenabled]>[dropzone-indicator]": {
              opacity: "1",
              cursor: "move"
            }
          }
        } : {
          "[dropzone]": {
            borderRadius: this.sizes.borderRadius,
            backgroundColor: this.colors.accent.shade(50),
            color: this.colors.on.accent.shade(50),
            transition: "background-color .2s",
            "&[dropenabled]": {
              backgroundColor: this.colors.accent.shade(100),
              color: this.colors.on.accent.shade(100)
            }
          }
        }
      }
    ];
  }
};
__decorateClass([
  property({ attribute: false })
], XBuildifyDropzone.prototype, "index", 2);
__decorateClass([
  property({ attribute: false })
], XBuildifyDropzone.prototype, "parentID", 2);
__decorateClass([
  property({ attribute: false })
], XBuildifyDropzone.prototype, "subItemID", 2);
__decorateClass([
  property({ attribute: "drag-over", type: Boolean })
], XBuildifyDropzone.prototype, "showOnDragOver", 2);
__decorateClass([
  property({ attribute: "no-swap", type: Boolean })
], XBuildifyDropzone.prototype, "noSwapIndexes", 2);
XBuildifyDropzone = __decorateClass([
  customElement("x-buildify-dropzone")
], XBuildifyDropzone);
export {
  XBuildifyDropzone
};
